import {
  ActionStore,
  ApiResourceLocation,
  createAction,
  DownloadStore,
  KeyStore,
  LazyRemoteCollectionStore,
  on,
  overrideDuplicateFeatureMergeStrategy,
  ParamsStore,
  SOFTLINE_FEATURE_DOWNLOAD,
  SOFTLINE_SERVICE_UUID,
  StoreBuilder,
  StoreFeature,
  SubscriptionStore,
  SyncedRemoteCollectionStore,
} from '@softline/core';
import { Product } from '../data/product';
import { KundenKonditionslisteService } from '../services/kunden-konditionsliste.service';
import { lastValueFrom } from 'rxjs';
import { PrintPayload } from '../data/print-payload';
import { SOFTLINE_API_KUNDEN_KONDITIONSLISTE_PRINT } from '../kunde-konditionliste.api';

export interface State extends SyncedRemoteCollectionStore.State<Product>, LazyRemoteCollectionStore.State<Product> {}

const lazyRemoteCollectionStore = LazyRemoteCollectionStore.create<Product>();

// tslint:disable-next-line:no-namespace
namespace KundenKonditionslisteStoreExtension {

  export const actions = {
    ...SubscriptionStore.actions,
    save: createAction<State, Product, any>('konditionsliste/save'),
    print: createAction<State, PrintPayload, DownloadStore.DownloadResult>('konditionsliste/print')
  };

  export const feature: Partial<StoreFeature<State>> = {
    actions: [
      on(actions.save, async ({featureName, injector, params, commit, get}) => {
        const service = injector.get(KundenKonditionslisteService);
        const token = injector.get(SOFTLINE_SERVICE_UUID)();

        commit(featureName, KeyStore.mutations.add, token);
        commit(featureName, ParamsStore.mutations.add, { key: token, params });

        const { id, ...productWithoutId } = params;

        const subState$ = SubscriptionStore.handleSubscriptionState(
          service.createOrUpdate(productWithoutId),
          featureName,
          commit,
          token
        );

        const result = await lastValueFrom(
          ActionStore.handleObservableActionState(
            subState$,
            featureName,
            commit,
            actions.save.name,
            token
          )
        );

        const updates: Product[] = [];

        for (const prod of result) {
          // We only update existing records immediately
          // If the API inserts records they have a different id, on update the id stays the same
          // Form date is still the same, so we just update the currently displayed ones and ignore insertions
          if (get(featureName, lazyRemoteCollectionStore.getters.entity, prod?.id ?? '')) {
            updates.push(prod);
          }
        }

        if (updates.length > 0)
          commit(featureName, lazyRemoteCollectionStore.mutations.updateMany, updates);
      }),
      on(actions.print, async ({ injector, params, commit, featureName, dispatch }) => {
        const token = injector.get(SOFTLINE_SERVICE_UUID)();

        const location: ApiResourceLocation = {
          path: SOFTLINE_API_KUNDEN_KONDITIONSLISTE_PRINT
        };

        return await dispatch(SOFTLINE_FEATURE_DOWNLOAD, DownloadStore.actions.download, {
          token,
          body: params,
          filename: 'Konditionsliste_' + params.valid_from + '.pdf',
          location
        });
      })
    ]
  };
}

export const KundenKonditionslisteStore = new StoreBuilder(lazyRemoteCollectionStore)
  .add(SyncedRemoteCollectionStore.create<Product>(), overrideDuplicateFeatureMergeStrategy)
  .add(KundenKonditionslisteStoreExtension)
  .value;
